import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'react-toastify';
import { NS, ORG_SETTINGS_KEYS } from '../../../../../i18n/constants/keys';
import { Button } from '../../../../../ui';
import { TextArea } from '../../../../../ui/input/text_area/text_area';
import { TextInput } from '../../../../../ui/input/text_input/text_input';
import {
  getOrganization,
  updateOrganization,
  deleteChildCompany,
} from '../../../../../utils/api_service/organization';
import { ChildCompany, Organization } from '../../../../../utils/types/admin_types';
import LoadingSpinner from '../../../../job_openings/create/ui/LoaderSpinner';
import { UploadBase64Image } from './UploadBase64Image';
import { MotionColorPicker } from '../../../../../ui/components/ColorPicker/MotionColorPicker';
import {
  DEFAULT_PRIMARY_COLOR,
  DEFAULT_SECONDARY_COLOR,
  applyBrandTheme,
  getContrastTextColor,
  getContrastRatio,
  hexToRgba,
  isValidHex,
  normalizeHex,
} from '../../../../../utils/theme/brandTheme';

// ─── Apple-style Icons ─────────────────────────────────────────────────────

const PaletteIcon = () => (
    <svg className='w-5 h-5' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
      <path
          strokeLinecap='round'
          strokeLinejoin='round'
          strokeWidth='2'
          d='M7 21a4 4 0 01-4-4V5a2 2 0 012-2h4a2 2 0 012 2v12a4 4 0 01-4 4zm0 0h12a2 2 0 002-2v-4a2 2 0 00-2-2h-2.343M11 7.343l1.657-1.657a2 2 0 012.828 0l2.829 2.829a2 2 0 010 2.828l-8.486 8.485M7 17h.01'
      />
    </svg>
);

const BuildingIcon = () => (
    <svg className='w-5 h-5' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
      <path
          strokeLinecap='round'
          strokeLinejoin='round'
          strokeWidth='2'
          d='M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4'
      />
    </svg>
);

const CheckShieldIcon = () => (
    <svg className='w-4 h-4 text-emerald-500' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
      <path
          strokeLinecap='round'
          strokeLinejoin='round'
          strokeWidth='2'
          d='M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z'
      />
    </svg>
);

const PlusIcon = () => (
    <svg className='w-4 h-4' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
      <path strokeLinecap='round' strokeLinejoin='round' strokeWidth='2.5' d='M12 4v16m8-8H4' />
    </svg>
);

const TrashIcon = () => (
    <svg className='w-4 h-4' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
      <path
          strokeLinecap='round'
          strokeLinejoin='round'
          strokeWidth='2'
          d='M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16'
      />
    </svg>
);

const ChevronDownIcon = () => (
    <svg className='w-4 h-4' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
      <path strokeLinecap='round' strokeLinejoin='round' strokeWidth='2' d='M19 9l-7 7-7-7' />
    </svg>
);

const ChevronUpIcon = () => (
    <svg className='w-4 h-4' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
      <path strokeLinecap='round' strokeLinejoin='round' strokeWidth='2' d='M5 15l7-7 7 7' />
    </svg>
);

export const NAVBAR_COLOR_PRESETS = [
  { name: 'Deep Forest (Default)', hex: '#093725' },
  { name: 'Midnight Navy', hex: '#0a192f' },
  { name: 'Space Slate', hex: '#1e293b' },
  { name: 'Deep Indigo', hex: '#1e1b4b' },
  { name: 'Deep Plum', hex: '#3b0764' },
  { name: 'Charcoal Black', hex: '#18181b' },
  { name: 'Clean White', hex: '#ffffff' },
  { name: 'Dark Teal', hex: '#042f2e' },
  { name: 'Dark Pine', hex: '#064e3b' },
  { name: 'Gunmetal Grey', hex: '#334155' },
];
export const SECONDARY_COLOR_PRESETS = NAVBAR_COLOR_PRESETS;

// ─── Empty child company template ────────────────────────────────────────────

const emptyChildCompany = (): ChildCompany => ({
  id: '',
  companyName: '',
  companyLogo: '',
  supportEmail: '',
  replyEmail: '',
  supportPhone: '',
  companyDetails: '',
  perksAndBenefits: '',
});

// ─── Child Company Row (Apple / glassmorphism styled) ────────────────────────

interface ChildCompanyRowProps {
  company: ChildCompany;
  index: number;
  isEditing: boolean;
  primaryColor: string;
  onUpdate: (index: number, field: keyof ChildCompany, value: string) => void;
  onRemove: (index: number) => void;
  onLogoChange: (index: number, base64: string) => void;
}

const ChildCompanyRow: React.FC<ChildCompanyRowProps> = ({
                                                           company,
                                                           index,
                                                           isEditing,
                                                           primaryColor,
                                                           onUpdate,
                                                           onRemove,
                                                           onLogoChange,
                                                         }) => {
  const [expanded, setExpanded] = useState(false);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    onUpdate(index, e.target.name as keyof ChildCompany, e.target.value);
  };

  return (
      <motion.div
          layout
          className='border border-slate-200/80 rounded-2xl overflow-hidden bg-white/70 backdrop-blur-xl shadow-sm'
      >
        {/* Row header — always visible */}
        <div
            className='flex items-center justify-between px-4 py-3 bg-slate-50/80 cursor-pointer hover:bg-slate-100/80 transition-colors'
            onClick={() => setExpanded((prev) => !prev)}
        >
          <div className='flex items-center gap-3 min-w-0'>
            {company.companyLogo ? (
                <img
                    src={company.companyLogo}
                    alt={company.companyName}
                    className='w-8 h-8 rounded-full object-cover border border-slate-200 bg-white flex-shrink-0'
                />
            ) : (
                <div
                    className='w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0'
                    style={{ backgroundColor: hexToRgba(primaryColor, 0.12), color: primaryColor }}
                >
                  <BuildingIcon />
                </div>
            )}
            <span className='font-bold text-slate-800 text-sm truncate'>
            {company.companyName || <span className='text-slate-400 italic font-medium'>Unnamed Company</span>}
          </span>
            {company.supportEmail && (
                <span className='hidden sm:inline text-xs text-slate-400 font-mono truncate'>
              {company.supportEmail}
            </span>
            )}
          </div>
          <div className='flex items-center gap-2 flex-shrink-0'>
            {isEditing && (
                <button
                    onClick={(e) => {
                      e.stopPropagation();
                      onRemove(index);
                    }}
                    className='p-1.5 rounded-lg text-red-400 hover:bg-red-50 hover:text-red-600 transition-colors'
                    title='Remove company'
                >
                  <TrashIcon />
                </button>
            )}
            <span className='text-slate-400'>{expanded ? <ChevronUpIcon /> : <ChevronDownIcon />}</span>
          </div>
        </div>

        {/* Expanded detail panel */}
        <AnimatePresence initial={false}>
          {expanded && (
              <motion.div
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: 'auto', opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  transition={{ duration: 0.25, ease: 'easeInOut' }}
                  className='overflow-hidden'
              >
                <div className='p-5 border-t border-slate-100 bg-white/60 text-left'>
                  <div className='grid grid-cols-1 md:grid-cols-3 gap-6'>
                    {/* Left: fields */}
                    <div className='md:col-span-2 space-y-4'>
                      {isEditing ? (
                          <>
                            <TextInput
                                name='companyName'
                                label='Company Name'
                                value={company.companyName}
                                type='text'
                                onChange={handleChange}
                            />
                            <div className='grid grid-cols-2 gap-4'>
                              <TextInput
                                  name='supportEmail'
                                  label='Support Email'
                                  value={company.supportEmail || ''}
                                  type='text'
                                  onChange={handleChange}
                              />
                              <TextInput
                                  name='replyEmail'
                                  label='Reply To Email'
                                  value={company.replyEmail || ''}
                                  type='text'
                                  onChange={handleChange}
                              />
                            </div>
                            <TextInput
                                name='supportPhone'
                                label='Support Phone'
                                value={company.supportPhone || ''}
                                type='text'
                                onChange={handleChange}
                            />
                            <div className='space-y-1'>
                              <label className='text-xs font-bold text-slate-500 uppercase tracking-wider'>
                                Perks & Benefits
                              </label>
                              <TextArea
                                  name='perksAndBenefits'
                                  value={company.perksAndBenefits || ''}
                                  onChange={handleChange}
                                  rows={4}
                              />
                            </div>
                            <div className='space-y-1'>
                              <label className='text-xs font-bold text-slate-500 uppercase tracking-wider'>
                                Company Overview
                              </label>
                              <TextArea
                                  name='companyDetails'
                                  value={company.companyDetails || ''}
                                  onChange={handleChange}
                                  rows={4}
                              />
                            </div>
                          </>
                      ) : (
                          <div className='space-y-4'>
                            <div className='grid grid-cols-2 gap-4'>
                              <div className='p-3 rounded-xl bg-slate-50/70 border border-slate-100'>
                                <p className='text-[10px] text-slate-400 font-bold uppercase tracking-wider'>
                                  Support Email
                                </p>
                                <p className='text-sm text-slate-800 font-mono mt-1'>{company.supportEmail || '—'}</p>
                              </div>
                              <div className='p-3 rounded-xl bg-slate-50/70 border border-slate-100'>
                                <p className='text-[10px] text-slate-400 font-bold uppercase tracking-wider'>
                                  Reply To Email
                                </p>
                                <p className='text-sm text-slate-800 font-mono mt-1'>{company.replyEmail || '—'}</p>
                              </div>
                              <div className='p-3 rounded-xl bg-slate-50/70 border border-slate-100'>
                                <p className='text-[10px] text-slate-400 font-bold uppercase tracking-wider'>
                                  Support Phone
                                </p>
                                <p className='text-sm text-slate-800 mt-1'>{company.supportPhone || '—'}</p>
                              </div>
                            </div>
                            <div>
                              <p className='text-[10px] text-slate-400 font-bold uppercase tracking-wider'>
                                Company Overview
                              </p>
                              <p className='text-sm text-slate-700 mt-1 whitespace-pre-line leading-relaxed'>
                                {company.companyDetails || 'No company details provided'}
                              </p>
                            </div>
                            <div>
                              <p className='text-[10px] text-slate-400 font-bold uppercase tracking-wider'>
                                Perks & Benefits
                              </p>
                              <p className='text-sm text-slate-700 mt-1 whitespace-pre-line leading-relaxed'>
                                {company.perksAndBenefits || 'No perks and benefits listed'}
                              </p>
                            </div>
                          </div>
                      )}
                    </div>

                    {/* Right: logo */}
                    <div className='bg-slate-50/80 rounded-2xl p-4 border border-slate-100'>
                      <UploadBase64Image
                          logo={company.companyLogo || ''}
                          setLogo={(base64) => onLogoChange(index, base64)}
                          isEditing={isEditing}
                      />
                    </div>
                  </div>
                </div>
              </motion.div>
          )}
        </AnimatePresence>
      </motion.div>
  );
};

// ─── Main Component ───────────────────────────────────────────────────────────

export const GeneralSettings = () => {
  const { t } = useTranslation(NS.organizationSettings);
  const [organization, setOrganization] = useState<Organization>({
    organizationName: '',
    supportEmail: '',
    replyEmail: '',
    supportPhone: '',
    companyLogo: '',
    perksAndBenefits: '',
    companyDetails: '',
    primaryColor: DEFAULT_PRIMARY_COLOR,
    secondaryColor: DEFAULT_SECONDARY_COLOR,
    childCompanies: [],
  });
  const [loading, setLoading] = useState<boolean>(true);
  const [isEditing, setIsEditing] = useState<boolean>(false);
  const [confirmUpdate, setConfirmUpdate] = useState<boolean>(false);
  const [saving, setSaving] = useState<boolean>(false);

  useEffect(() => {
    const fetchOrganization = async () => {
      try {
        const response = await getOrganization();
        if (response.ok) {
          const data: Organization = await response.json();
          const primary =
              data.primaryColor ||
              data.brandColor ||
              localStorage.getItem('primaryColor') ||
              localStorage.getItem('brandPrimaryColor') ||
              DEFAULT_PRIMARY_COLOR;

          const secondary =
              data.secondaryColor ||
              localStorage.getItem('secondaryColor') ||
              localStorage.getItem('brandSecondaryColor') ||
              DEFAULT_SECONDARY_COLOR;

          const loadedOrg: Organization = {
            ...data,
            primaryColor: normalizeHex(primary, DEFAULT_PRIMARY_COLOR),
            secondaryColor: normalizeHex(secondary, DEFAULT_SECONDARY_COLOR),
            childCompanies: data.childCompanies ?? [],
          };

          setOrganization(loadedOrg);

          // Apply loaded theme to DOM and CSS variables
          applyBrandTheme({
            primaryColor: loadedOrg.primaryColor,
            secondaryColor: loadedOrg.secondaryColor,
          });
        }
      } catch (err) {
        console.error('Failed to load organization settings', err);
      } finally {
        setLoading(false);
      }
    };

    fetchOrganization();
  }, []);

  const handleChange = (
      event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = event.target;
    setOrganization((prevState) => ({
      ...prevState,
      [name]: value,
    }));
  };

  const setCompanyLogo = (base64Url: string) => {
    setOrganization((prevState) => ({
      ...prevState,
      companyLogo: base64Url,
    }));
  };

  const handlePrimaryColorChange = (newColor: string) => {
    setOrganization((prevState) => {
      const updated = {
        ...prevState,
        primaryColor: newColor,
        brandColor: newColor,
      };
      applyBrandTheme({
        primaryColor: newColor,
        secondaryColor: updated.secondaryColor,
      });
      return updated;
    });
  };

  const handleSecondaryColorChange = (newColor: string) => {
    setOrganization((prevState) => {
      const updated = {
        ...prevState,
        secondaryColor: newColor,
      };
      applyBrandTheme({
        primaryColor: updated.primaryColor,
        secondaryColor: newColor,
      });
      return updated;
    });
  };

  const isCustomColors =
      normalizeHex(organization.primaryColor || DEFAULT_PRIMARY_COLOR).toLowerCase() !==
      normalizeHex(DEFAULT_PRIMARY_COLOR).toLowerCase() ||
      normalizeHex(organization.secondaryColor || DEFAULT_SECONDARY_COLOR).toLowerCase() !==
      normalizeHex(DEFAULT_SECONDARY_COLOR).toLowerCase();

  const handleResetToDefaults = () => {
    if (!isEditing) return;
    setOrganization((prevState) => {
      const updated = {
        ...prevState,
        primaryColor: DEFAULT_PRIMARY_COLOR,
        secondaryColor: DEFAULT_SECONDARY_COLOR,
        brandColor: DEFAULT_PRIMARY_COLOR,
      };
      applyBrandTheme({
        primaryColor: DEFAULT_PRIMARY_COLOR,
        secondaryColor: DEFAULT_SECONDARY_COLOR,
      });
      return updated;
    });
    toast.info('Palette reset to HiAcuity defaults (Primary #038912 & Navbar #093725)', {
      position: 'bottom-center',
    });
  };

  // ── Child company handlers ───────────────────────────────────────────────

  const addChildCompany = () => {
    if (!isEditing) return;
    setOrganization((prev) => ({
      ...prev,
      childCompanies: [...(prev.childCompanies ?? []), emptyChildCompany()],
    }));
  };

  const removeChildCompany = async (index: number) => {
    const child = organization.childCompanies?.[index];

    if (child?.id) {
      // Existing child — delete from backend
      try {
        const response = await deleteChildCompany(child.id);
        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          toast.error(errorData.error || 'Failed to remove child company', { position: 'bottom-center' });
          return;
        }
      } catch (err: any) {
        toast.error(err?.message || 'An unexpected error occurred', { position: 'bottom-center' });
        return;
      }
    }

    // Remove from local state regardless
    setOrganization((prev) => ({
      ...prev,
      childCompanies: prev.childCompanies?.filter((_, i) => i !== index),
    }));
  };

  const updateChildCompany = (index: number, field: keyof ChildCompany, value: string) => {
    setOrganization((prev) => {
      const updated = [...(prev.childCompanies ?? [])];
      updated[index] = { ...updated[index], [field]: value };
      return { ...prev, childCompanies: updated };
    });
  };

  const updateChildLogo = (index: number, base64: string) => {
    updateChildCompany(index, 'companyLogo', base64);
  };

  const childCompanies = organization.childCompanies ?? [];

  // ── Submit ───────────────────────────────────────────────────────────────

  const updateOrg = async () => {
    if (!confirmUpdate) {
      toast.warning(t(ORG_SETTINGS_KEYS.messages.confirmBeforeUpdate), {
        position: 'bottom-center',
      });
      return;
    }

    setSaving(true);
    const validPrimary = isValidHex(organization.primaryColor || '')
        ? normalizeHex(organization.primaryColor || '', DEFAULT_PRIMARY_COLOR)
        : DEFAULT_PRIMARY_COLOR;

    const validSecondary = isValidHex(organization.secondaryColor || '')
        ? normalizeHex(organization.secondaryColor || '', DEFAULT_SECONDARY_COLOR)
        : DEFAULT_SECONDARY_COLOR;

    // Immediately persist and apply theme locally
    if (organization.companyLogo) {
      localStorage.setItem('companyLogo', organization.companyLogo);
    }
    applyBrandTheme({
      primaryColor: validPrimary,
      secondaryColor: validSecondary,
    });

    const payload: Organization = {
      organizationName: organization.organizationName || '',
      supportEmail: organization.supportEmail || '',
      replyEmail: organization.replyEmail || '',
      supportPhone: organization.supportPhone || '',
      companyLogo: organization.companyLogo || '',
      perksAndBenefits: organization.perksAndBenefits || '',
      companyDetails: organization.companyDetails || '',
      primaryColor: validPrimary,
      secondaryColor: validSecondary,
      brandColor: validPrimary,
      childCompanies: organization.childCompanies || [],
    };

    try {
      const response = await updateOrganization(payload);
      if (response.ok) {
        toast.success(t(ORG_SETTINGS_KEYS.messages.updateSuccess) || 'Organization updated successfully', {
          position: 'bottom-center',
        });
        setIsEditing(false);
        setConfirmUpdate(false);
      } else {
        const errorData = await response.json().catch(() => ({}));
        toast.error(errorData.error || errorData.message || 'Failed to update organization', {
          position: 'bottom-center',
        });
      }
    } catch (err: any) {
      toast.error(err?.message || 'An unexpected error occurred', {
        position: 'bottom-center',
      });
    } finally {
      setSaving(false);
    }
  };

  const currentPrimaryColor = organization.primaryColor || DEFAULT_PRIMARY_COLOR;
  const currentSecondaryColor = organization.secondaryColor || DEFAULT_SECONDARY_COLOR;

  const primaryContrastText = getContrastTextColor(currentPrimaryColor);
  const secondaryContrastText = getContrastTextColor(currentSecondaryColor);

  const primaryWcag = getContrastRatio(currentPrimaryColor, primaryContrastText);
  const secondaryWcag = getContrastRatio(currentSecondaryColor, secondaryContrastText);

  return (
      <>
        {loading ? (
            <div className='flex flex-col justify-center items-center min-h-[50vh] gap-3'>
              <LoadingSpinner label='' />
              <p className='text-xs font-semibold text-slate-400 tracking-wide uppercase'>
                Loading Organization Profile...
              </p>
            </div>
        ) : (
            <motion.div
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.35, ease: 'easeOut' }}
                className='w-full space-y-6 pb-20'
            >
              {/* Apple-styled Glassmorphism Header Bar */}
              <motion.div
                  layout
                  className='bg-white/80 backdrop-blur-2xl border border-slate-200/90 rounded-3xl p-6 sm:p-7 shadow-sm flex flex-col sm:flex-row items-start sm:items-center justify-between gap-5'
              >
                <div className='space-y-1.5 text-left'>
                  <div className='flex flex-wrap items-center gap-3'>
                    <h1 className='text-2xl font-bold text-slate-900 tracking-tight'>
                      {organization?.organizationName || 'Organization Profile'}
                    </h1>
                    <motion.div
                        layout
                        className='px-3 py-1 rounded-full text-[11px] font-bold tracking-wider uppercase flex items-center gap-2 shadow-sm'
                        style={{
                          background: `linear-gradient(135deg, ${currentPrimaryColor} 0%, ${currentSecondaryColor} 100%)`,
                          color: '#ffffff',
                        }}
                    >
                      <span className='w-1.5 h-1.5 rounded-full bg-white animate-ping' />
                      <span>Dual Theme Active</span>
                    </motion.div>
                  </div>
                  <p className='text-xs text-slate-500 font-medium leading-relaxed max-w-2xl'>
                    Manage company branding, dynamic primary and secondary color palettes, contact profiles, and organizational styling across HiAcuity.
                  </p>
                </div>

                {/* Apple iOS-Style Mode Switch */}
                <div className='flex items-center gap-3 bg-slate-100/90 p-1.5 rounded-2xl border border-slate-200/80 shadow-inner'>
              <span className='text-xs font-semibold text-slate-600 pl-2 select-none'>
                {isEditing
                    ? t(ORG_SETTINGS_KEYS.actions.exitEditMode)
                    : t(ORG_SETTINGS_KEYS.actions.editOrganization)}
              </span>

                  <button
                      type='button'
                      role='switch'
                      aria-checked={isEditing}
                      onClick={() => setIsEditing(!isEditing)}
                      className={`relative inline-flex h-7 w-12 items-center rounded-full transition-colors duration-300 focus:outline-none focus:ring-2 focus:ring-offset-2 ${
                          isEditing ? 'focus:ring-offset-2' : 'bg-slate-300 focus:ring-slate-400'
                      }`}
                      style={{
                        backgroundColor: isEditing ? currentPrimaryColor : undefined,
                      }}
                  >
                    <motion.span
                        layout
                        transition={{ type: 'spring', damping: 25, stiffness: 350 }}
                        className={`inline-block h-5 w-5 transform rounded-full bg-white shadow-md transition-transform duration-300 ${
                            isEditing ? 'translate-x-6' : 'translate-x-1'
                        }`}
                    />
                  </button>
                </div>
              </motion.div>

              {/* Main Grid Content */}
              <div className='grid grid-cols-1 lg:grid-cols-3 gap-6'>
                {/* Left 2-Columns: Theming & Profile Details */}
                <div className='lg:col-span-2 space-y-6'>
                  {/* Brand Theme Customization Card */}
                  <motion.div
                      layout
                      className='bg-white/85 backdrop-blur-2xl border border-slate-200/90 rounded-3xl p-6 sm:p-7 shadow-sm space-y-5 hover:shadow-md transition-shadow relative z-30'
                  >
                    <div className='flex items-center justify-between border-b border-slate-100 pb-4'>
                      <div className='flex items-center gap-3'>
                        <div
                            className='p-2.5 rounded-2xl text-white shadow-sm transition-colors duration-300'
                            style={{
                              background: `linear-gradient(135deg, ${currentPrimaryColor} 0%, ${currentSecondaryColor} 100%)`,
                            }}
                        >
                          <PaletteIcon />
                        </div>
                        <div className='text-left'>
                          <h2 className='text-base font-bold text-slate-900 tracking-tight'>
                            Brand Colors & Dynamic Theming
                          </h2>
                          <p className='text-xs text-slate-500'>
                            Customize primary brand color and top navbar color using physics-based motion color wheels.
                          </p>
                        </div>
                      </div>

                      <div className='flex items-center gap-2'>
                        {isCustomColors && isEditing && (
                            <motion.button
                                whileHover={{ scale: 1.03 }}
                                whileTap={{ scale: 0.97 }}
                                type='button'
                                onClick={handleResetToDefaults}
                                className='px-3 py-1.5 rounded-xl text-xs font-semibold bg-slate-900 text-white hover:bg-slate-800 shadow-sm flex items-center gap-1.5 transition-all'
                                title='Reset both primary and navbar colors to default'
                            >
                              <svg className='w-3.5 h-3.5' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
                                <path strokeLinecap='round' strokeLinejoin='round' strokeWidth='2.5' d='M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15' />
                              </svg>
                              <span>Reset to Default Colors</span>
                            </motion.button>
                        )}
                        <span className='text-[10px] font-bold px-2.5 py-1 rounded-full uppercase tracking-wider bg-emerald-50 text-emerald-700 border border-emerald-100'>
                      PUT /organization
                    </span>
                      </div>
                    </div>

                    {/* Dual Color Pickers: Primary & Secondary */}
                    <div className='grid grid-cols-1 md:grid-cols-2 gap-4 relative z-40'>
                      {/* Primary Color Picker */}
                      <div className='space-y-1.5 relative z-30'>
                        <div className='flex items-center justify-between text-left px-1'>
                      <span className='text-xs font-bold text-slate-800 tracking-tight'>
                        Primary Color
                      </span>
                          <span className='text-[10px] font-semibold text-slate-400'>
                        Buttons, Accents, Links
                      </span>
                        </div>
                        <MotionColorPicker
                            color={currentPrimaryColor}
                            onChange={handlePrimaryColorChange}
                            isEditing={isEditing}
                            label='Primary Color'
                            description='Main interactive accents & buttons'
                            defaultColor={DEFAULT_PRIMARY_COLOR}
                            popoverAlign='left'
                        />
                      </div>

                      {/* Navbar Color Picker */}
                      <div className='space-y-1.5 relative z-20'>
                        <div className='flex items-center justify-between text-left px-1'>
                      <span className='text-xs font-bold text-slate-800 tracking-tight'>
                        Navbar Color
                      </span>
                          <span className='text-[10px] font-semibold text-slate-400'>
                        Top Navigation Bar & Header
                      </span>
                        </div>
                        <MotionColorPicker
                            color={currentSecondaryColor}
                            onChange={handleSecondaryColorChange}
                            isEditing={isEditing}
                            label='Navbar Color'
                            description='Top navigation bar & header background'
                            presetColors={NAVBAR_COLOR_PRESETS}
                            defaultColor={DEFAULT_SECONDARY_COLOR}
                            popoverAlign='right'
                        />
                      </div>
                    </div>

                    {/* Live Brand Theme Application Preview Box */}
                    <div className='bg-slate-50/90 rounded-2xl p-5 border border-slate-200/70 space-y-4 shadow-inner text-left'>
                      <div className='flex items-center justify-between'>
                        <div className='flex items-center gap-2'>
                      <span className='text-[11px] font-bold text-slate-400 uppercase tracking-wider'>
                        Live System Theme Preview
                      </span>
                          <span
                              className={`text-[10px] font-bold px-2 py-0.5 rounded-full border ${
                                  isCustomColors
                                      ? 'bg-amber-50 text-amber-700 border-amber-200'
                                      : 'bg-emerald-50 text-emerald-700 border-emerald-200'
                              }`}
                          >
                        {isCustomColors ? 'Custom Brand Palette' : 'Default HiAcuity Palette'}
                      </span>
                        </div>
                        <div className='flex items-center gap-2 font-mono text-[11px] font-bold text-slate-600'>
                      <span className='flex items-center gap-1'>
                        <span className='text-[10px] font-sans text-slate-400'>Primary:</span>
                        <span
                            className='w-2.5 h-2.5 rounded-full inline-block border border-black/10'
                            style={{ backgroundColor: currentPrimaryColor }}
                        />
                        {currentPrimaryColor.toUpperCase()}
                      </span>
                          <span className='text-slate-300'>•</span>
                          <span className='flex items-center gap-1'>
                        <span className='text-[10px] font-sans text-slate-400'>Navbar:</span>
                        <span
                            className='w-2.5 h-2.5 rounded-full inline-block border border-black/10'
                            style={{ backgroundColor: currentSecondaryColor }}
                        />
                            {currentSecondaryColor.toUpperCase()}
                      </span>
                        </div>
                      </div>

                      {/* Interactive UI Elements Preview */}
                      <div className='flex flex-wrap items-center gap-3'>
                        {/* Primary Button Preview */}
                        <motion.button
                            whileHover={{ scale: 1.03 }}
                            whileTap={{ scale: 0.97 }}
                            type='button'
                            className='px-4 py-2 text-xs font-semibold rounded-xl shadow-md transition-all flex items-center gap-1.5'
                            style={{
                              backgroundColor: currentPrimaryColor,
                              color: primaryContrastText,
                            }}
                        >
                          <span>Primary Action</span>
                          <span className='text-[10px] opacity-80'>
                        ({primaryWcag.toFixed(1)}:1)
                      </span>
                        </motion.button>

                        {/* Top Navbar Preview Pill */}
                        <motion.div
                            whileHover={{ scale: 1.03 }}
                            whileTap={{ scale: 0.97 }}
                            className='px-4 py-2 text-xs font-semibold rounded-xl shadow-md transition-all flex items-center gap-1.5 border border-black/5'
                            style={{
                              backgroundColor: currentSecondaryColor,
                              color: secondaryContrastText,
                            }}
                        >
                          <span>Top Navbar</span>
                          <span className='text-[10px] opacity-80'>
                        ({secondaryWcag.toFixed(1)}:1)
                      </span>
                        </motion.div>

                        {/* Gradient Theme Pill */}
                        <motion.div
                            whileHover={{ scale: 1.04 }}
                            className='px-4 py-2 text-xs font-bold rounded-xl text-white shadow-md flex items-center gap-1.5 select-none'
                            style={{
                              background: `linear-gradient(135deg, ${currentPrimaryColor} 0%, ${currentSecondaryColor} 100%)`,
                            }}
                        >
                          <span>Brand Gradient Blend</span>
                        </motion.div>

                        {/* Active Status Badge */}
                        <motion.span
                            whileHover={{ scale: 1.05 }}
                            className='px-3 py-1.5 rounded-full text-xs font-bold border flex items-center gap-1.5 shadow-xs select-none'
                            style={{
                              backgroundColor: hexToRgba(currentPrimaryColor, 0.12),
                              color: currentPrimaryColor,
                              borderColor: hexToRgba(currentPrimaryColor, 0.25),
                            }}
                        >
                      <span
                          className='w-1.5 h-1.5 rounded-full'
                          style={{ backgroundColor: currentPrimaryColor }}
                      />
                          <span>Active Status</span>
                        </motion.span>
                      </div>
                    </div>
                  </motion.div>

                  {/* General Organization Details Card */}
                  <motion.div
                      layout
                      className='bg-white/85 backdrop-blur-2xl border border-slate-200/90 rounded-3xl p-6 sm:p-7 shadow-sm space-y-6 hover:shadow-md transition-shadow relative z-10'
                  >
                    <div className='border-b border-slate-100 pb-4 flex items-center justify-between text-left'>
                      <div className='flex items-center gap-3'>
                        <div className='p-2.5 rounded-2xl bg-slate-100 text-slate-700 shadow-sm'>
                          <BuildingIcon />
                        </div>
                        <div>
                          <h2 className='text-base font-bold text-slate-900 tracking-tight'>
                            General Information
                          </h2>
                          <p className='text-xs text-slate-500'>
                            Company contact emails, address details, and support communication.
                          </p>
                        </div>
                      </div>

                      <span className='text-[11px] font-bold px-2.5 py-1 rounded-full uppercase tracking-wider bg-slate-100 text-slate-600 border border-slate-200'>
                    {isEditing ? 'Editing Mode' : 'Read Only'}
                  </span>
                    </div>

                    {isEditing ? (
                        <motion.div
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            className='grid grid-cols-1 sm:grid-cols-2 gap-4'
                        >
                          <div className='sm:col-span-2'>
                            <TextInput
                                name='organizationName'
                                label={t(ORG_SETTINGS_KEYS.labels.companyName)}
                                value={organization?.organizationName || ''}
                                type='text'
                                onChange={handleChange}
                            />
                          </div>
                          <TextInput
                              name='supportEmail'
                              label={t(ORG_SETTINGS_KEYS.labels.supportEmail)}
                              value={organization?.supportEmail || ''}
                              type='text'
                              onChange={handleChange}
                          />
                          <TextInput
                              name='replyEmail'
                              label={t(ORG_SETTINGS_KEYS.labels.replyToEmail)}
                              value={organization?.replyEmail || ''}
                              type='text'
                              onChange={handleChange}
                          />
                          <div className='sm:col-span-2'>
                            <TextInput
                                name='supportPhone'
                                label={t(ORG_SETTINGS_KEYS.labels.supportPhone)}
                                value={organization?.supportPhone || ''}
                                type='text'
                                onChange={handleChange}
                            />
                          </div>
                        </motion.div>
                    ) : (
                        <div className='grid grid-cols-1 sm:grid-cols-2 gap-4 text-left'>
                          <div className='space-y-1 p-4 rounded-2xl bg-slate-50/70 border border-slate-100'>
                            <h3 className='text-[11px] font-bold text-slate-400 uppercase tracking-wider'>
                              {t(ORG_SETTINGS_KEYS.labels.companyName)}
                            </h3>
                            <p className='text-sm font-bold text-slate-800'>
                              {organization?.organizationName || 'N/A'}
                            </p>
                          </div>

                          <div className='space-y-1 p-4 rounded-2xl bg-slate-50/70 border border-slate-100'>
                            <h3 className='text-[11px] font-bold text-slate-400 uppercase tracking-wider'>
                              {t(ORG_SETTINGS_KEYS.labels.supportEmail)}
                            </h3>
                            <p className='text-sm font-medium text-slate-700 font-mono'>
                              {organization?.supportEmail || 'N/A'}
                            </p>
                          </div>

                          <div className='space-y-1 p-4 rounded-2xl bg-slate-50/70 border border-slate-100'>
                            <h3 className='text-[11px] font-bold text-slate-400 uppercase tracking-wider'>
                              {t(ORG_SETTINGS_KEYS.labels.replyToEmail)}
                            </h3>
                            <p className='text-sm font-medium text-slate-700 font-mono'>
                              {organization?.replyEmail || 'N/A'}
                            </p>
                          </div>

                          <div className='space-y-1 p-4 rounded-2xl bg-slate-50/70 border border-slate-100'>
                            <h3 className='text-[11px] font-bold text-slate-400 uppercase tracking-wider'>
                              {t(ORG_SETTINGS_KEYS.labels.supportPhone)}
                            </h3>
                            <p className='text-sm font-medium text-slate-700'>
                              {organization?.supportPhone || 'N/A'}
                            </p>
                          </div>
                        </div>
                    )}

                    {/* Additional Text Areas */}
                    <div className='space-y-5 pt-3 border-t border-slate-100 text-left'>
                      <div className='space-y-2'>
                        <h3 className='text-xs font-bold text-slate-700 uppercase tracking-wider'>
                          {t(ORG_SETTINGS_KEYS.labels.perksAndBenefits)}
                        </h3>
                        {isEditing ? (
                            <TextArea
                                name='perksAndBenefits'
                                value={organization?.perksAndBenefits || ''}
                                onChange={handleChange}
                                rows={5}
                            />
                        ) : (
                            <div className='p-4 rounded-2xl bg-slate-50/80 border border-slate-100 text-xs text-slate-700 leading-relaxed whitespace-pre-line'>
                              {organization?.perksAndBenefits ||
                                  t(ORG_SETTINGS_KEYS.empty.noPerks)}
                            </div>
                        )}
                      </div>

                      <div className='space-y-2'>
                        <h3 className='text-xs font-bold text-slate-700 uppercase tracking-wider'>
                          {t(ORG_SETTINGS_KEYS.labels.companyDetails)}
                        </h3>
                        {isEditing ? (
                            <TextArea
                                name='companyDetails'
                                value={organization?.companyDetails || ''}
                                onChange={handleChange}
                                rows={4}
                            />
                        ) : (
                            <div className='p-4 rounded-2xl bg-slate-50/80 border border-slate-100 text-xs text-slate-700 leading-relaxed whitespace-pre-line'>
                              {organization?.companyDetails ||
                                  t(ORG_SETTINGS_KEYS.empty.noCompanyDetails)}
                            </div>
                        )}
                      </div>
                    </div>
                  </motion.div>

                  {/* Child Companies Card */}
                  <motion.div
                      layout
                      className='bg-white/85 backdrop-blur-2xl border border-slate-200/90 rounded-3xl p-6 sm:p-7 shadow-sm space-y-5 hover:shadow-md transition-shadow relative z-10'
                  >
                    <div className='border-b border-slate-100 pb-4 flex items-center justify-between text-left'>
                      <div className='flex items-center gap-3'>
                        <div
                            className='p-2.5 rounded-2xl shadow-sm'
                            style={{ backgroundColor: hexToRgba(currentPrimaryColor, 0.12), color: currentPrimaryColor }}
                        >
                          <BuildingIcon />
                        </div>
                        <div>
                          <h2 className='text-base font-bold text-slate-900 tracking-tight'>Child Companies</h2>
                          <p className='text-xs text-slate-500'>
                            {childCompanies.length > 0
                                ? `${childCompanies.length} compan${childCompanies.length === 1 ? 'y' : 'ies'} under this organisation`
                                : 'No child companies added yet'}
                          </p>
                        </div>
                      </div>

                      {isEditing && (
                          <motion.button
                              whileHover={{ scale: 1.03 }}
                              whileTap={{ scale: 0.97 }}
                              type='button'
                              onClick={addChildCompany}
                              className='flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-bold shadow-sm transition-all'
                              style={{
                                backgroundColor: hexToRgba(currentPrimaryColor, 0.12),
                                color: currentPrimaryColor,
                                border: `1px solid ${hexToRgba(currentPrimaryColor, 0.25)}`,
                              }}
                          >
                            <PlusIcon />
                            <span>Add Company</span>
                          </motion.button>
                      )}
                    </div>

                    {childCompanies.length > 0 ? (
                        <div className='space-y-2'>
                          <AnimatePresence initial={false}>
                            {childCompanies.map((company, index) => (
                                <ChildCompanyRow
                                    key={index}
                                    company={company}
                                    index={index}
                                    isEditing={isEditing}
                                    primaryColor={currentPrimaryColor}
                                    onUpdate={updateChildCompany}
                                    onRemove={removeChildCompany}
                                    onLogoChange={updateChildLogo}
                                />
                            ))}
                          </AnimatePresence>
                        </div>
                    ) : (
                        isEditing && (
                            <div
                                onClick={addChildCompany}
                                className='border-2 border-dashed border-slate-200 rounded-2xl py-10 flex flex-col items-center gap-2 cursor-pointer hover:border-slate-300 hover:bg-slate-50/70 transition-colors group'
                            >
                              <div
                                  className='p-2 rounded-full transition-colors'
                                  style={{ backgroundColor: hexToRgba(currentPrimaryColor, 0.1), color: currentPrimaryColor }}
                              >
                                <PlusIcon />
                              </div>
                              <p className='text-sm text-slate-400 group-hover:text-slate-600'>
                                Click to add your first child company
                              </p>
                            </div>
                        )
                    )}
                  </motion.div>
                </div>

                {/* Right Column: Logo Upload Box */}
                <div className='lg:col-span-1'>
                  <motion.div
                      layout
                      className='bg-white/85 backdrop-blur-2xl border border-slate-200/90 rounded-3xl p-6 sm:p-7 shadow-sm sticky top-6 space-y-4 hover:shadow-md transition-shadow'
                  >
                    <div className='border-b border-slate-100 pb-3 flex items-center justify-between text-left'>
                      <h2 className='text-sm font-bold text-slate-900 tracking-tight'>
                        Company Branding Logo
                      </h2>
                      <CheckShieldIcon />
                    </div>

                    <UploadBase64Image
                        logo={organization.companyLogo || ''}
                        setLogo={setCompanyLogo}
                        isEditing={isEditing}
                    />
                  </motion.div>
                </div>
              </div>

              {/* Apple-style Edit Mode Floating / Sticky Actions Drawer */}
              <AnimatePresence>
                {isEditing && (
                    <motion.div
                        initial={{ opacity: 0, y: 30, scale: 0.96 }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        exit={{ opacity: 0, y: 30, scale: 0.96 }}
                        transition={{ type: 'spring', damping: 25, stiffness: 320 }}
                        className='sticky bottom-6 z-40 bg-white/95 backdrop-blur-2xl border border-slate-200 shadow-2xl rounded-3xl p-4 sm:p-5 flex flex-col sm:flex-row items-center justify-between gap-4 max-w-4xl mx-auto'
                    >
                      <div className='flex items-center gap-3'>
                        <input
                            type='checkbox'
                            id='confirmUpdate'
                            checked={confirmUpdate}
                            onChange={(e) => setConfirmUpdate(e.target.checked)}
                            className='h-4 w-4 rounded-md border-slate-300 cursor-pointer'
                            style={{ accentColor: currentPrimaryColor }}
                        />
                        <label
                            htmlFor='confirmUpdate'
                            className='text-xs font-bold text-slate-700 cursor-pointer select-none'
                        >
                          {t(ORG_SETTINGS_KEYS.labels.confirmUpdate)}
                        </label>
                      </div>

                      <div className='flex items-center gap-3 w-full sm:w-auto justify-end'>
                        <Button
                            label={t(ORG_SETTINGS_KEYS.actions.cancel)}
                            onClick={() => {
                              setIsEditing(false);
                              setConfirmUpdate(false);
                            }}
                            variant='secondary'
                            className='hover:bg-slate-50 text-slate-700 rounded-xl'
                        />
                        <Button
                            label={
                              saving
                                  ? 'Saving...'
                                  : t(ORG_SETTINGS_KEYS.actions.updateOrganization)
                            }
                            onClick={updateOrg}
                            variant='primary'
                            disabled={!confirmUpdate || saving}
                            className={`rounded-xl transition-all ${
                                confirmUpdate && !saving
                                    ? 'shadow-md opacity-100'
                                    : 'opacity-50 cursor-not-allowed'
                            }`}
                        />
                      </div>
                    </motion.div>
                )}
              </AnimatePresence>
            </motion.div>
        )}
      </>
  );
};